import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import useWizardStore from '@zs/useWizardStore';
import cache, { BlobImageSet } from '@/cache';
import {
  Coord,
  LayoutRow,
  getArrayColor,
  getStrokeColor,
} from '@t/LayoutTypes';
import { ImageRegistration } from '@t/ImagingTypes';
import * as common from '@/common';
import * as elements from '@svg/elements';
import {
  LocData,
} from '@svg/elements';
import {
  octSettings,
  octBoundsDeg,
} from '@/imageFiles';
import { renderPolarGrid } from './VisualFieldCanvas';


interface ReviewLayoutProps {
  csv: LayoutRow[],
  marked?: number[],
  prl?: { OD: Coord, OS: Coord },
  cyclo?: { OD: number, OS: number },
}
export const ReviewLayout = ({
  csv,
  marked,
  prl,
  cyclo,
}: ReviewLayoutProps): JSX.Element => {
  const studyEye = useWizardStore(state => state.studyEye);
  const registrations = useWizardStore(state => state.registrations);
  const fovealPit = useWizardStore(state => state.fovealPit);
  // const cacheLoaded = useWizardStore(state => state.cacheLoaded);

  const locs = useMemo(() => csv.map(row => ({
    x: row.stimulus_x_deg,
    y: row.stimulus_y_deg,
    id: row.stimulus_location_id,
    arrayID: row.array_id,
    diameter: row.stimulus_diameter_deg,
    isMarked: !marked ? false : marked.includes(row.stimulus_location_id),
    fill: getArrayColor(row.array_id),
    stroke: getStrokeColor(row.array_id),
  }) as LocData), [csv, marked]);

  const fovOD = elements.getFoveaDeg(fovealPit.OD, cache.images.OD.BSCAN.crop.width);
  const fovOS = elements.getFoveaDeg(fovealPit.OS, cache.images.OS.BSCAN.crop.width);

  return (
    <div className='flex gap-2'>
      <div>
        {renderLayout('OD', locs.filter(l => l.id > 0), cache.images.OD, registrations.OD, fovOD, prl?.OD, cyclo?.OD)}
        <div className='text-sm'>{`OD (${studyEye == 'OD' ? '' : 'non-'}study eye)`}</div>
      </div>
      <div>
        {renderLayout('OS', locs.filter(l => l.id < 0), cache.images.OS, registrations.OS, fovOS, prl?.OS, cyclo?.OS)}
        <div className='text-sm'>{`OS (${studyEye == 'OS' ? '' : 'non-'}study eye)`}</div>
      </div>
    </div>
  );
};


const strokeWidthDeg = elements.strokeWidthDeg;
const scanBox = elements.renderScanBox();

function renderLayout(eye: string, locs: LocData[], blobs: BlobImageSet, reg: ImageRegistration, fovDeg: Coord, prl?: Coord, cyclo?: number) {
  const overlayScale = octSettings.enfaceAngle / blobs.RED.crop.width;
  let fixationTransform = '';
  if (cyclo) fixationTransform = `rotate(${cyclo})`;
  if (prl) fixationTransform = `translate(${prl.x}, ${prl.y}) ` + fixationTransform;

  return (
    <svg key={`vvpgrid-${eye}`} width={480} height={360}>
      <rect x="-100%" y="-100%" width="400%" height="400%" fill="#404040" />
      <g transform={`translate(${240 + (eye == 'OD' ? -32 : 32)}, ${180})`}>
        <g transform='scale(11)' style={{ pointerEvents: 'none' }}>
          <g key='imaging' transform={fixationTransform}>
            <g transform={`translate(${-fovDeg.x}, ${-fovDeg.y})`}>
              <g transform={`scale(${overlayScale})`}>
                {elements.makeRegTransform(reg.FAFONH, elements.renderCropped(blobs.FAFONH.url, blobs.FAFONH.crop))}
                {elements.makeRegTransform(reg.FAF, elements.renderCropped(blobs.FAF.url, blobs.FAF.crop))}
              </g>
              {elements.renderFoveal(fovDeg)}
              {scanBox}
            </g>
          </g>
          <g key='loc-data' strokeWidth={strokeWidthDeg}>
            {locs.map(elements.renderLoc)}
            <g fill='none' stroke='white'>
              {locs.filter(l => l.isMarked).map(elements.renderMark)}
            </g>
          </g>
        </g>
        <g opacity={0.5} fill="none" stroke='white' strokeWidth={10*strokeWidthDeg} style={{ pointerEvents: "none" }}>
          {renderPolarGrid(11)}
        </g>
      </g>
    </svg>
  );
}
